import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { flat, parseEntity } from '../entity/entity';
import { newUlid } from '../entity/catalog';
import type { Arc, Status } from '../plan/arc';
import { heldBy, innerArcNames, openInside } from '../plan/arc';
import { renderMarkdown } from '../plan/markdown';
import { StatusGlyph } from '../board/StatusGlyph';
import { StatusPicker } from '../board/StatusPicker';
import { EditableName } from '../design/EditableName';
import { shortDate, shortWhen } from '../design/dates';
import { mintedAt } from '../plan/chronology';
import { arcHref } from '../routes/ArcRoute';
import { hrefFor } from '../routes/EntityPage';
import { useVault } from '../session/useVault';
import { pendingText } from '../vault/writeQueue';
import { itemFor } from '../thoughts/corpus';
import { defaultNote, faceOf, itemKind, noteTitle } from '../nook/nook';
import { NookWriting } from '../nook/NookWriting';
import { useTagGround } from '../design/useTagGround';
import { listOf } from '../entity/entity';
import { monthPrefix, newHeldNoteFile, newNotePath } from '../writing/textNote';
import type { MintedNote } from '../writing/useNoteText';
import { useWorkspaces } from '../workspaces/useWorkspaces';
import { nameOf } from '../workspaces/workspace';
import { useCatalog } from '../entity/catalogStore';
import { AddIntoDialog } from '../membership/AddDialogs';
import { withItemsAdded } from '../membership/membership';
import { arcDocument, arcProse, evidenceOf, sourcePaths, withArcNote, withArcProse } from './doc';
import { EditableProse } from './EditableProse';
import { Face } from './Face';
import { DocPage, HeadLine, Switches } from './DocPage';
import type { MarginGroup, MarginRow } from './DocPage';
import { isArcIn } from '../vault/kinds';
import '../nook/nook.css';

const DOCKED_KEY = 'arc.writing.docked';

type Tab = 'log' | 'evidence' | 'plane';

/**
 * An arc as a document: its name; what it is waiting on you for, when it is,
 * as the loudest line on the page; its first step and its end; its log and
 * evidence behind quiet switches, opening above the prose rather than after
 * it; its prose, written where it is read; and where it belongs in the margin.
 *
 * Write opens the arc's default note beside the column — a note the arc
 * holds, apart from its own prose.
 */
export function ArcPage({
  arc,
  arcs,
  onSetStatus,
  onSetArchived,
  onRename,
  onRemoveMember,
}: {
  arc: Arc;
  arcs: Arc[];
  onSetStatus: (status: Status, evidence?: string) => void;
  onSetArchived: (archived: boolean) => void;
  onRename: (name: string) => void;
  /** Absent where the surface cannot write. Takes the member out of this
   *  arc's `arcs:`; the member's own file is left alone. */
  onRemoveMember?: (member: Arc) => void;
}) {
  const tags = listOf(arc.frontMatter['tags']);
  const ground = useTagGround(tags[0]);
  const navigate = useNavigate();
  const { state, write, commit } = useVault();
  const catalog = useCatalog();
  const nooks = useWorkspaces();
  const [tab, setTab] = useState<Tab | null>(null);
  const [picking, setPicking] = useState<DOMRect | null>(null);
  const [adding, setAdding] = useState(false);
  const [writing, setWriting] = useState<{ path: string | null } | null>(null);
  const [docked, setDocked] = useState(() => readDocked());
  const lastNote = useRef<string | null>(null);

  const { log } = useMemo(() => arcDocument(arc), [arc]);
  const passages = useMemo(() => arcProse(arc.raw), [arc.raw]);
  /** The file as last written from here: a keystroke can arrive before the
   *  plan hands the arc back, and each passage is rewritten from this. */
  const latest = useRef(arc.raw);
  useEffect(() => {
    latest.current = arc.raw;
  }, [arc.raw]);
  const writePassage = (index: number, next: string) => {
    const change = (t: string) => withArcProse(t, index, next);
    const text = change(latest.current);
    if (text === latest.current) return;
    latest.current = text;
    write([{ path: arc.path, text, change }]);
  };
  const sources = useMemo(() => sourcePaths(arc), [arc]);
  const byName = useMemo(() => new Map(arcs.map((a) => [a.name, a])), [arcs]);

  // Everything the arc holds that is not an arc, read in one batch — a note
  // or a recording says what it is only from its own file.
  const [files, setFiles] = useState<ReadonlyMap<string, string | null>>(new Map());
  const wanted = useMemo(
    () => [...new Set(sources.map((s) => s.path))].filter((p) => !isArcIn(p, arcs)).sort(),
    [sources, arcs],
  );
  const wantedKey = wanted.join('\n');
  useEffect(() => {
    if (state.status !== 'ready' || wanted.length === 0) return;
    let cancelled = false;
    void state.vault.readFiles(wanted).then((read) => {
      if (cancelled) return;
      const byPath = new Map(read.map((r) => [r.path, r.text ?? null]));
      setFiles((current) => {
        const next = new Map(current);
        for (const p of wanted) next.set(p, pendingText(p) ?? byPath.get(p) ?? current.get(p) ?? null);
        return next;
      });
    });
    return () => {
      cancelled = true;
    };
    // `wanted` is tracked by its key: a new array with the same paths is not a
    // reason to read again.
  }, [state, wantedKey]);

  const notes = useMemo(
    () => wanted.filter((p) => itemKind(p, files.get(p) ?? null) === 'note'),
    [wanted, files],
  );

  /** The note Write opens: `write:` names it by id, and the note's own file
   *  declares the id it was made with. */
  const declared = defaultNote(arc.frontMatter);
  const defaultPath = useMemo(() => {
    if (!declared) return null;
    for (const p of notes) {
      const text = files.get(p);
      if (text && flat(parseEntity(p, text).frontMatter['id']).trim() === declared) return p;
    }
    return null;
  }, [declared, notes, files]);

  /**
   * A note made on the first word: the file declaring its id and that the web
   * wrote it, held by the arc through `sources:`, and made the one Write opens
   * — all in one write, so no device sees a note the arc does not hold.
   */
  const mint = useCallback(
    (first: string): MintedNote | null => {
      if (state.status !== 'ready') return null;
      const now = new Date();
      const taken = new Set(state.vault.listPaths(monthPrefix(now)));
      const path = newNotePath(now, (c) => taken.has(c) || files.has(c));
      const id = newUlid(now.getTime());
      const note = newHeldNoteFile(first, id);
      const holding = (t: string) => withArcNote(t, path, id);
      setFiles((m) => new Map(m).set(path, note));
      write([
        { path, text: note },
        { path: arc.path, text: holding(arc.raw), change: holding },
      ]);
      lastNote.current = path;
      setWriting({ path });
      return { path, raw: note };
    },
    [state, files, arc, write],
  );

  const openNote = (path: string | null) => {
    lastNote.current = path;
    setWriting({ path });
  };

  const toggleDock = () =>
    setDocked((d) => {
      try {
        localStorage.setItem(DOCKED_KEY, d ? '0' : '1');
      } catch {
        // Not remembered; it docks again next time.
      }
      return !d;
    });

  const kindAt = (path: string) => itemKind(path, files.get(path) ?? null);

  const margin: MarginGroup[] = [
    {
      label: 'Part of',
      rows: heldBy(arc, arcs).map((s) => arcRow(s, arcs)),
    },
    {
      label: 'Holds',
      rows: innerArcNames(arc)
        .map((n) => byName.get(n))
        .filter((a): a is Arc => !!a)
        .map((a) => ({
          ...arcRow(a, arcs),
          menu: onRemoveMember ? [{ label: 'Remove from storyline', run: () => onRemoveMember(a) }] : undefined,
        })),
      add: state.status === 'ready' ? { label: 'Add…', onClick: () => setAdding(true) } : undefined,
    },
    {
      label: 'Held by',
      rows:
        nooks.status === 'ready'
          ? nooks.workspaces
              .filter((w) => !w.archived && w.arcs.includes(arc.name))
              .map((w) => {
                const { name, named } = nameOf(w, catalog);
                return {
                  key: w.path,
                  label: name,
                  unnamedNook: named ? undefined : w,
                  glyph: { kind: 'nook' as const, tags: w.tags },
                  to: hrefFor(w.path),
                  tags: w.tags,
                };
              })
          : [],
    },
    {
      label: 'Notes',
      rows: notes.map((p) => ({
        key: p,
        label: files.get(p) ? noteTitle(p, files.get(p)!) : bare(p),
        glyph: { kind: 'note' as const },
        onOpen: () => openNote(p),
        tags: catalog.get(p)?.tags,
      })),
    },
    {
      label: 'From',
      rows: fromRows(sources, kindAt, arcs, (p) => catalog.get(p)?.tags),
    },
  ];

  const status = arc.status;
  const needs = flat(arc.frontMatter['needs']);
  const startWith = flat(arc.frontMatter['start-with']);
  const doneWhen = flat(arc.frontMatter['done-when']);
  const evidence = evidenceOf(arc);
  const face = useMemo(() => faceOf(arc.path, arc.raw), [arc.path, arc.raw]);
  const created = mintedAt(arc) ? new Date(mintedAt(arc)) : null;
  const links = {
    resolveArc: (title: string) => {
      const target = arcs.find((a) => a.title === title);
      return target ? arcHref(target.name) : null;
    },
  };

  const writingPanel = writing && (
    <NookWriting
      path={writing.path}
      docked={docked}
      onToggleDock={toggleDock}
      onClose={() => setWriting(null)}
      mint={mint}
      onChange={(p, text) => setFiles((m) => new Map(m).set(p, text))}
      label="A note on this arc"
      onNewNote={() => openNote(null)}
      className="doc-writing"
    >
      {notes.some((p) => p !== writing.path) && (
        <>
          <span className="doc-group-label">Notes on this arc</span>
          {notes
            .filter((p) => p !== writing.path)
            .map((p) => (
              <button key={p} type="button" className="doc-row" onClick={() => openNote(p)}>
                <span className="doc-row-glyph">
                  <span className="doc-glyph doc-glyph-note" />
                </span>
                <span className="doc-row-label">{files.get(p) ? noteTitle(p, files.get(p)!) : bare(p)}</span>
              </button>
            ))}
        </>
      )}
    </NookWriting>
  );

  return (
    <DocPage
      ground={ground}
      tags={tags}
      fallback="/plan"
      gaveWay={writing !== null && docked}
      menu={[
        arc.archived
          ? { label: 'Restore', run: () => onSetArchived(false) }
          : { label: 'Archive', run: () => onSetArchived(true) },
      ]}
      actions={
        <button
          type="button"
          className="doc-btn is-action"
          aria-pressed={writing !== null}
          onClick={() => (writing ? setWriting(null) : openNote(lastNote.current ?? defaultPath))}
        >
          <PenGlyph /> Write
        </button>
      }
      margin={margin}
      footnote={created ? `Created ${shortDate(created)}` : undefined}
      writing={writingPanel}
    >
      <h1 className="doc-title">
        <EditableName name={arc.title} onRename={onRename} />
      </h1>

      <div className="doc-head">
        {status === 'needs-input' ? (
          <button type="button" className="doc-ask" onClick={(e) => setPicking(e.currentTarget.getBoundingClientRect())}>
            <StatusGlyph status={status} size={14} />
            <span>{needs ? `Waiting on you — ${needs}` : 'Waiting on you'}</span>
          </button>
        ) : (
          <button type="button" className="doc-state" onClick={(e) => setPicking(e.currentTarget.getBoundingClientRect())}>
            <StatusGlyph status={status} size={13} />
            <span>{STATUS_WORDS[status]}</span>
          </button>
        )}
        {startWith && status !== 'done' && <HeadLine lead="Start with">{startWith}</HeadLine>}
        {doneWhen && <HeadLine lead="Ends when">{doneWhen}</HeadLine>}
      </div>

      <Face lines={face} className="is-quiet" />

      <Switches<Tab>
        options={[
          { key: 'log', label: 'Log' },
          { key: 'evidence', label: 'Evidence' },
          { key: 'plane', label: 'Canvas', go: () => navigate(`${arcHref(arc.name)}/plane`) },
        ]}
        open={tab}
        onOpen={setTab}
      />

      {tab === 'log' && (
        <div className="doc-body is-soft">{log ? renderMarkdown(log, links) : <p>No log yet.</p>}</div>
      )}
      {tab === 'evidence' && (
        <div className="doc-body is-soft">
          {evidence.length > 0 ? (
            <ul>
              {evidence.map((line, i) => (
                <li key={i}>{line}</li>
              ))}
            </ul>
          ) : (
            <p>No evidence yet.</p>
          )}
        </div>
      )}

      {/* Each passage is written where it is read, inside its own markers;
          Write, beside the column, is for notes the arc holds. */}
      {passages.map((passage, i) => (
        <EditableProse
          key={passage.id ?? i}
          text={passage.text}
          onChange={(next) => writePassage(i, next)}
          links={links}
          label="The arc"
          placeholder="Write about this arc"
        />
      ))}

      {adding && (
        <AddIntoDialog
          holder={{ kind: 'arc', path: arc.path, raw: arc.raw }}
          onAdd={(items) => {
            const change = (t: string) => withItemsAdded({ kind: 'arc', path: arc.path, raw: t }, items);
            const text = change(arc.raw);
            if (text !== arc.raw) void commit([{ path: arc.path, text, change }]);
          }}
          onClose={() => setAdding(false)}
        />
      )}

      {picking && (
        <StatusPicker
          arc={arc}
          anchor={picking}
          onClose={() => setPicking(null)}
          onPick={(next, withEvidence) => {
            onSetStatus(next, withEvidence);
            setPicking(null);
          }}
          onArchive={(archived) => {
            onSetArchived(archived);
            setPicking(null);
          }}
        />
      )}
    </DocPage>
  );
}

const STATUS_WORDS: Record<Status, string> = {
  active: 'active',
  'needs-input': 'waiting on you',
  next: 'next',
  parked: 'parked',
  done: 'done',
};

function arcRow(arc: Arc, all: readonly Arc[]): MarginRow {
  const inside = openInside(arc, all);
  return {
    key: arc.path,
    label: arc.title,
    meta: inside > 0 ? `${inside} inside` : undefined,
    glyph: { kind: 'status', status: arc.status },
    to: arcHref(arc.name),
    tags: listOf(arc.frontMatter['tags']),
  };
}

/**
 * Where the arc came from, other than the notes it holds: a recording or one
 * of its topics as when it was said, an arc as its state and name, anything
 * else by its file's name. A recording cited by several topics is one row.
 */
function fromRows(
  sources: { path: string; anchor: string | null }[],
  kindAt: (path: string) => string,
  all: readonly Arc[],
  tagsAt: (path: string) => readonly string[] | undefined,
): MarginRow[] {
  const seen = new Set<string>();
  const rows: MarginRow[] = [];
  for (const { path } of sources) {
    if (seen.has(path)) continue;
    seen.add(path);
    if (isArcIn(path, all)) {
      const arc = all.find((a) => a.path === path);
      if (arc) rows.push(arcRow(arc, all));
      else rows.push({ key: path, label: bare(path), glyph: { kind: 'none' } });
      continue;
    }
    const kind = kindAt(path);
    if (kind === 'note') continue;
    const at = itemFor(path).at;
    rows.push({
      key: path,
      label: kind === 'recording' && at ? shortWhen(at) : bare(path),
      glyph: kind === 'recording' ? { kind: 'recording' } : { kind: 'none' },
      to: hrefFor(path),
      tags: tagsAt(path),
    });
  }
  return rows;
}

function bare(path: string): string {
  return path.replace(/^.*\//, '').replace(/\.md$/, '');
}

function readDocked(): boolean {
  try {
    return localStorage.getItem(DOCKED_KEY) !== '0';
  } catch {
    return true;
  }
}

function PenGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 20h4L19 9l-4-4L4 16v4z" />
    </svg>
  );
}
